import { useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { CopyToClipboardButton } from '#src/atoms/buttons/CopyToClipboardButton/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconApple } from '#src/atoms/icons/system/iconApple.ts';
import { Horizontal, Vertical } from '#src/atoms/layout/flex/index.tsx';
import { BodyM, HeadlineM } from '#src/atoms/typography/index.tsx';
import { Alert } from '#src/atoms/ui/Alert/index.tsx';
import { Dialog } from '#src/components/dialog/index.tsx';
import { useCurrentUserContext } from '#src/contexts/currentUser/index.ts';
import { useScreenSize } from '#src/hooks/device/useScreenSize.ts';
import { useRetainedValue } from '#src/hooks/useRetainedValue.ts';
import { isApplePrivateRelayEmail } from '#src/lib/email.ts';
import { tabletAndAbove } from '#src/style/mediaQuery.ts';

const SCA_MESSAGE = 'sorare:mangopay:sca-complete';

const Content = styled(Vertical).attrs({ gap: 1.5 })`
  height: 100%;
`;
const Iframe = styled.iframe`
  display: block;
  border: none;
  width: 100%;
  height: 100%;
  margin: auto;

  @media ${tabletAndAbove} {
    width: 500px;
    height: 600px;
    max-width: 100%;
    max-height: 100%;
  }
`;

type ContentProps = {
  url: string;
  onSuccess: () => void;
  onFailed: () => void;
};

const MangopayScaIframeContent = ({
  url,
  onSuccess,
  onFailed,
}: ContentProps) => {
  const { currentUser } = useCurrentUserContext();
  const email = currentUser?.email;
  const showPrivateRelayHint = !!email && isApplePrivateRelayEmail(email);

  useEffect(() => {
    const cb = (event: MessageEvent) => {
      if (event.data.eventId !== SCA_MESSAGE) return;
      const params = new URL(event.data.data.location).searchParams;
      const controlStatus = params.get('controlStatus');
      if (controlStatus === 'VALIDATED') {
        onSuccess();
      } else if (controlStatus === 'REFUSED' || controlStatus === 'FAILED') {
        onFailed();
      }
    };
    window.addEventListener('message', cb);
    return () => window.removeEventListener('message', cb);
  }, [onSuccess, onFailed]);

  return (
    <Content>
      {!!showPrivateRelayHint && (
        <Alert
          icon={
            <FontAwesomeIcon icon={iconApple} color="var(--c-yellow-400)" />
          }
          title={
            <FormattedMessage defaultMessage="If you're asked for your email" />
          }
          message={
            <Vertical gap={0.5}>
              <BodyM>
                <FormattedMessage defaultMessage="Apple hides your real email address. If verification asks for your email, use the anonymized address below." />
              </BodyM>
              <Horizontal gap={1}>
                <BodyM bold>{email}</BodyM>
                <CopyToClipboardButton textToCopy={email} />
              </Horizontal>
            </Vertical>
          }
        />
      )}
      <Iframe
        src={url}
        allow="publickey-credentials-get https://sca.sandbox.mangopay.com/ https://sca.mangopay.com/; publickey-credentials-create https://sca.sandbox.mangopay.com/ https://sca.mangopay.com/;"
      />
    </Content>
  );
};

type Props = {
  open: boolean;
  url: string | null;
  onSuccess: () => void;
  onFailed: () => void;
  onClose: () => void;
};

export const MangopayScaIframeDialog = ({
  open,
  url,
  onSuccess,
  onFailed,
  onClose,
}: Props) => {
  const { up: isTablet } = useScreenSize('tablet');
  const retainedUrl = useRetainedValue(url);

  return (
    <Dialog
      open={open}
      disableBackdropClick
      disableEscapeKeyDown
      title={
        <HeadlineM as="h4" className="text-center">
          <FormattedMessage defaultMessage="Account security" />
        </HeadlineM>
      }
      onClose={onClose}
      maxWidth={false}
      fullScreen={!isTablet}
    >
      {!!retainedUrl && (
        <MangopayScaIframeContent
          url={retainedUrl}
          onSuccess={onSuccess}
          onFailed={onFailed}
        />
      )}
    </Dialog>
  );
};
